import {
    collection,
    doc,
    getDocs,
    updateDoc,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { User } from '../types';

/**
 * Get all users from Firestore
 */
export async function getAllUsers(): Promise<User[]> {
    try {
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);

        const users: User[] = [];

        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            users.push({
                uid: docSnap.id,
                email: data.email || '',
                displayName: data.displayName || '',
                role: data.role || 'user',
                isApproved: data.isApproved ?? false,
                allowedApps: data.allowedApps || [],
                createdAt: data.createdAt || Timestamp.now(),
            } as User);
        });

        // Sort by createdAt descending (most recent first)
        users.sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || 0;
            const bTime = b.createdAt?.toMillis?.() || 0;
            return bTime - aTime;
        });

        return users;
    } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
    }
}

/**
 * Approve a user (allow access to the service)
 */
export async function approveUser(userId: string): Promise<void> {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            isApproved: true,
        });
    } catch (error) {
        console.error('Error approving user:', error);
        throw error;
    }
}

/**
 * Revoke a user's access (remove approval)
 */
export async function revokeUser(userId: string): Promise<void> {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            isApproved: false,
        });
    } catch (error) {
        console.error('Error revoking user:', error);
        throw error;
    }
}

/**
 * Update user's allowed apps
 */
export async function updateUserAllowedApps(userId: string, allowedApps: string[]): Promise<void> {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            allowedApps,
        });
    } catch (error) {
        console.error('Error updating user allowed apps:', error);
        throw error;
    }
}

/**
 * Delete a user from the system (Firebase Auth + Firestore)
 * This calls a Cloud Function because Firebase Auth deletion requires Admin SDK
 */
export async function deleteUser(userId: string): Promise<void> {
    try {
        // Import functions dynamically to avoid circular dependencies
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const deleteUserFn = httpsCallable(functions, 'deleteUser');

        const result = await deleteUserFn({ userId });
        const data = result.data as { success: boolean; message: string };

        if (!data.success) {
            throw new Error(data.message || 'Failed to delete user');
        }
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
}

